import { useEffect } from 'react';
import { darkTheme as MuiDarkTheme } from '@locus-taxy/encore-mui';

/**
 * Hook to add title headers to popup menus for NavNode items
 * This ensures navigation popups have proper headers with icons and titles
 */
export const useTitleHeaders = (navigationSignature: string) => {
  useEffect(() => {
    const addPopupHeaders = () => {
      const popups = document.querySelectorAll(
        '.MuiDrawer-root .MuiListItem-root > .MuiBox-root .MuiPaper-root'
      );
      popups.forEach(popup => {
        const paperEl = popup as HTMLElement;
        // Check if this popup already has a header
        if (paperEl.querySelector('.popup-header')) {
          return;
        }

        // Find the parent list item to get the title and icon
        const listItem = paperEl.closest('.MuiListItem-root');
        if (listItem) {
          const button = listItem.querySelector('.MuiListItemButton-root');
          if (button) {
            const titleElement = button.querySelector('.MuiTypography-root');
            const iconElement = button.querySelector(
              '.MuiSvgIcon-root, .MuiListItemIcon-root svg'
            );
            if (titleElement) {
              const title = titleElement.textContent ?? '';
              if (title) {
                // Create header element
                const header = document.createElement('div');
                header.className = 'popup-header';
                header.style.cssText = `
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 12px 16px;
                  font-size: 14px;
                  font-weight: 600;
                  border-bottom: 1px solid ${MuiDarkTheme.palette.divider};
                  background-color: ${MuiDarkTheme.palette.background.default};
                  white-space: nowrap;
                  overflow: hidden;
                `;

                // Create icon container
                const iconContainer = document.createElement('div');
                iconContainer.style.cssText = `
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 20px;
                  height: 20px;
                  flex-shrink: 0;
                `;

                // Clone the icon if it exists
                if (iconElement) {
                  const clonedIcon = iconElement.cloneNode(true) as HTMLElement;
                  clonedIcon.style.cssText = `
                    width: 20px;
                    height: 20px;
                    color: inherit;
                  `;
                  iconContainer.appendChild(clonedIcon);
                }

                // Create title text container
                const titleContainer = document.createElement('div');
                titleContainer.style.cssText = `
                  flex: 1;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `;
                titleContainer.textContent = title;

                // Assemble the header
                header.appendChild(iconContainer);
                header.appendChild(titleContainer);

                // Insert header at the beginning of the popup
                const listRoot = paperEl.querySelector('.MuiList-root');
                if (listRoot) {
                  paperEl.insertBefore(header, listRoot);
                }
              }
            }
          }
        }
      });
    };

    // Run immediately
    addPopupHeaders();

    // Set up observer to watch for new popups
    const observer = new MutationObserver(() => {
      addPopupHeaders();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [navigationSignature]);
};
